'use client';

import 'moment/locale/pl';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useDisclosure } from '@mantine/hooks';
import classNames from 'classnames';
import Image from 'next/image';
import { useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';

import Breadcrumb from '@/components/Breadcrumb';
import { useCartClient } from '@/components/Cart/hooks/useCartClient';
import ModalPopup from '@/components/ModalPopup';
import ProductSafePurchaseBadge from '@/components/ProductSafePurchaseBadge';
import SocialProofSection from '@/components/SocialProofSection';
import USPSection from '@/components/USPSection';
import { useProductGallery } from '@/contexts/GalleryModalContext';
import ScreenWidthContext from '@/contexts/ScreenWidthContext';
import {
  ProductButton,
  ProductDetails,
  ProductImages,
  SimilarProducts,
  useOmnibusPrice,
} from '@/domain/product';
import { productsBreadcrumbsItems } from '@/utils/const';
import { formatPrice, getProductPrices } from '@/utils/getPrice';
import { useShippingOptionsByIds } from '@/utils/hooks/useShippingOptionsByIds';
import DiamondIcon from 'public/icons/diamond.svg';
import deliveryIcon from 'public/icons/truck-fast.svg';

import styles from './styles.module.scss';
import { ProductPageProps } from './types';

const ProductPage = ({
  data: product,
  categoryProducts,
  storeProducts,
}: ProductPageProps) => {
  const [isPopUpVisible, setIsPopUpVisible] = useState(false);
  const { isMobile } = useContext(ScreenWidthContext);
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const { toggleGallery } = useProductGallery();
  const { omnibusPrice } = useOmnibusPrice(product.id);
  const { salePrice, originalPrice, rawOriginalPrice, rawSalePrice } =
    getProductPrices(product);

  const deliveryMethods = product.metadata.delivery.map(d => d.method);
  const isClassics = product.metadata.klasyki.is_klasyki;

  const isProductSold = product.status === 'sold';

  const { title, thumbnail, id, status } = product;
  const { data: shippingOptions } = useShippingOptionsByIds(deliveryMethods);

  const { addItemToCart, cart } = useCartClient();

  const isItemAlreadyInCart = useMemo(() => {
    const items = cart?.items.find(item => item.variant.product_id === id);
    return items;
  }, [cart]);

  const categoryName = product.category.name;
  const categoryHandle = product.category.handle;

  const subcategoryName = product.subcategory.name;
  const subcategoryHandle = product.subcategory.handle;
  const subcategoryAbsolutePath = product.subcategory.absolute_path;

  const breadcrumbsOptions = useMemo(() => {
    if (!categoryHandle) {
      return productsBreadcrumbsItems;
    }

    const itemsArray = [...productsBreadcrumbsItems];

    itemsArray.push({
      title: categoryName,
      href: `/k/${categoryHandle}/`,
    });

    if (!subcategoryHandle && !subcategoryAbsolutePath) {
      return itemsArray;
    }

    if (subcategoryAbsolutePath) {
      itemsArray.push({
        title: subcategoryName,
        href: `/k/${subcategoryAbsolutePath}`,
      });
    } else {
      itemsArray.push({
        title: subcategoryName,
        href: `/k/${categoryHandle}/${subcategoryHandle}/`,
      });
    }

    if (!title) {
      return itemsArray;
    }

    if (subcategoryAbsolutePath) {
      itemsArray.push({
        title: title,
        href: `/k/${subcategoryAbsolutePath}/${title}/`,
      });
    } else {
      itemsArray.push({
        title: title,
        href: `/k/${categoryHandle}/${subcategoryHandle}/${title}/`,
      });
    }
    return itemsArray;
  }, [
    categoryHandle,
    categoryName,
    subcategoryAbsolutePath,
    subcategoryHandle,
    subcategoryName,
    title,
  ]);

  const handleGalleryOpenChange = (isOpen: boolean) => {
    setIsGalleryOpen(isOpen);
    toggleGallery(isOpen);
  };

  useEffect(() => {
    if (product && rawOriginalPrice) {
      ReactGA.event('view_item', {
        currency: 'PLN',
        value: salePrice ? rawSalePrice / 100 : rawOriginalPrice! / 100,
        items: [
          {
            item_id: product.id,
            item_name: title,
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const backToShop = useMemo(() => {
    if (!categoryHandle) {
      return;
    }
    if (subcategoryAbsolutePath) {
      return `/k/${subcategoryAbsolutePath}/`;
    }

    const href = `/k/${categoryHandle}/${subcategoryHandle}/`;

    return href;
  }, [categoryHandle, subcategoryAbsolutePath, subcategoryHandle]);

  const selectedVariant = product.variants[0];
  const [stockQuantity, setStockQuantity] = useState(
    selectedVariant.inventory_quantity,
  );

  useEffect(() => {
    if (isItemAlreadyInCart) {
      const quantity =
        selectedVariant.inventory_quantity - isItemAlreadyInCart?.quantity;
      setStockQuantity(quantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isItemAlreadyInCart]);

  const handleSubtractQty = () => {
    setSelectedQuantity(prevVal => {
      return prevVal !== 1 ? prevVal - 1 : 1;
    });
  };

  const handleAddQty = () => {
    setSelectedQuantity(prevVal => {
      return prevVal < stockQuantity ? prevVal + 1 : stockQuantity;
    });
  };

  const handleAddToCart = () => {
    if (stockQuantity > 0 && status === 'published' && rawOriginalPrice) {
      addItemToCart(selectedVariant.id, selectedQuantity);

      ReactGA.event('add_to_cart', {
        currency: 'PLN',
        value: salePrice ? rawSalePrice / 100 : rawOriginalPrice! / 100,
        items: [
          {
            item_id: product.id,
            item_name: title,
            quantity: selectedQuantity,
          },
        ],
      });

      open();
    }
  };

  const renderOmnibusPrice = useMemo(() => {
    if (salePrice) {
      return (
        <p
          className={styles.omnibusPrice}
        >{`Najniższa cena z 30 dni przed obniżką: ${originalPrice}`}</p>
      );
    }
  }, [omnibusPrice]);

  const cheapestShippingOption = useMemo(() => {
    if (!shippingOptions) return null;
    if (shippingOptions.length === 1) return shippingOptions[0];
    return [...shippingOptions]
      .filter(e => e.name !== 'Odbiór osobisty' && e.name !== 'Odbior osobisty')
      .sort((a, b) => a.amount - b.amount)[0];
  }, [shippingOptions]);
  const minimumShippingPrice = useMemo(
    () => formatPrice(cheapestShippingOption?.amount ?? 1900),
    [cheapestShippingOption?.amount],
  );
  const isShippingOptionInfoVisible = useMemo(() => {
    return !(
      shippingOptions?.length === 1 &&
      shippingOptions[0].name.toLowerCase().includes('osobisty')
    );
  }, [shippingOptions]);

  useEffect(() => {
    document.documentElement.style.overflow = isGalleryOpen ? 'hidden' : 'auto';
  }, [isGalleryOpen]);

  return (
    <>
      <section className={styles.breadcrumbContainer}>
        <Breadcrumb
          items={breadcrumbsOptions}
          isMobile={isMobile}
          mobileOption={subcategoryName}
          mobileOptionSrc={backToShop}
        />
      </section>
      <section
        className={styles.productInfoContainer}
        style={{
          zIndex: isPopUpVisible ? 10000 : 'auto',
          position: isPopUpVisible ? 'relative' : 'initial',
        }}
      >
        <div className={styles.productImagesContainer}>
          <ProductImages
            handleGalleryOpenChange={handleGalleryOpenChange}
            isClassics={isClassics}
            isMobile={isMobile}
            isOnSale={!!salePrice}
            isProductSold={isProductSold}
            product={product}
          />
          <ProductDetails
            className={styles.productDetailsDesktop}
            isMobile={isMobile}
            isGalleryOpen={isGalleryOpen}
            product={product}
            shippingOptions={shippingOptions}
            store={product.store}
          />
        </div>
        {!isGalleryOpen && (
          <div className={styles.rightColWrapper}>
            <div className={styles.titleAttributesWrapper}>
              <div className={styles.titleWrapper}>
                <h1 className={styles.title}>{title}</h1>
              </div>
              {stockQuantity === 1 && (
                <div className={styles.uniqueProduct}>
                  <Image src={DiamondIcon} alt="Ikona diamentu" />
                  Unikat - tylko jedna sztuka!
                </div>
              )}
            </div>
            <div className={styles.priceWrapper}>
              {salePrice ? (
                <>
                  <div className={styles.salePriceWrapper}>
                    <p className={styles.salePrice}>{salePrice}</p>
                    <p
                      className={classNames(styles.price, styles.inactivePrice)}
                    >
                      {originalPrice}
                    </p>
                  </div>
                  {renderOmnibusPrice}
                </>
              ) : (
                <p className={styles.price}>{originalPrice}</p>
              )}
              {isProductSold && (
                <p className={styles.soldStatus}>Produkt wyprzedany</p>
              )}
            </div>
            <div className={styles.shippingInfoContainer}>
              {isShippingOptionInfoVisible && (
                <div>
                  <Image src={deliveryIcon} alt="Ikona dostawy" />
                  Dostawa od {minimumShippingPrice}
                </div>
              )}
              <div>
                <ClockIcon />
                {product?.store?.name === 'Boketto'
                  ? 'Wysyłka do 14 dni'
                  : 'Wysyłka w ciągu 7 dni roboczych'}
              </div>
            </div>
            <ProductButton
              handleAddQty={handleAddQty}
              handleAddToCart={handleAddToCart}
              handleSubtractQty={handleSubtractQty}
              isProductSold={isProductSold}
              product={product}
              selectedQuantity={selectedQuantity}
              stockQuantity={stockQuantity}
            />
            <div className={styles.safePurchaseBadgeLine} />
            <ProductSafePurchaseBadge
              onPopUpVisibilityChange={setIsPopUpVisible}
              storeId={product.store_id}
            />
            <ProductDetails
              className={styles.productDetailsMobile}
              isMobile={isMobile}
              isGalleryOpen={isGalleryOpen}
              product={product}
              shippingOptions={shippingOptions}
              store={product.store}
            />
            <ModalPopup
              quantity={selectedQuantity}
              title={title}
              product={product}
              thumbnail={thumbnail}
              opened={opened}
              close={close}
            />
          </div>
        )}
      </section>
      <SimilarProducts
        title="Podobne przedmioty"
        isGalleryOpen={isGalleryOpen}
        link={`/k/${product.category.handle}`}
        products={categoryProducts}
        className={styles.otherProductsFromStoreContainer}
      />
      <SocialProofSection />
      <SimilarProducts
        title="Więcej od tego sprzedawcy"
        isGalleryOpen={isGalleryOpen}
        link={`/vendors/${product.store.handle}/`}
        products={storeProducts}
        className={classNames(styles.otherProductsFromStoreContainer, {
          [styles.paddingNone]: categoryProducts.length === 0,
        })}
      />
      <USPSection />
    </>
  );
};

export default ProductPage;
